/**
 * Iter53 P32 — SaleOverview/atoms.jsx
 *
 * Componente prezentaționale extrase din SaleOverview.jsx (1727 LOC → 1300+ LOC) pentru:
 *   - reutilizare în alte pagini (SaleDetail, future Customer pages)
 *   - reduce risk de regresii când se modifică o singură secțiune
 *   - clarifică ce e „shared atom" vs „business component"
 *
 * Conține:
 *   - helpers: fmt (date/datetime), initials, truncateMid, PALETTES
 *   - FieldRow, SectionCard, PersonChip, NotesBoInline
 *   - constants: SUPPLIER_STATUS_OPTIONS
 *
 * Toate componentele primesc date prin props — fără state global, fără context.
 * Pentru SupplierStatusSelect (auto-save dropdown) → folosește direct în render-ul ContractCard
 * (necesită contract.id + onChange refresh) — păstrat inline în SaleOverview.jsx.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { EmptyValue } from "../EmptyValue";
import { formatRoDate, formatRoDateTime } from "../../lib/dates";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// =========================== format helpers ===========================
export const fmt = {
  date: formatRoDate,
  datetime: formatRoDateTime,
};

export const initials = (name) => {
  if (!name) return "—";
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0] || "").join("").toUpperCase();
};

export const truncateMid = (s, max = 14) => {
  if (!s || s.length <= max) return s;
  const head = Math.ceil(max / 2);
  return `${s.slice(0, head)}…${s.slice(-4)}`;
};

export const PALETTES = [
  { bg: "#EEEDFE", fg: "#3C3489" },
  { bg: "#E1F5EE", fg: "#085041" },
  { bg: "#F1EFE8", fg: "#444441" },
  { bg: "#FEE9DC", fg: "#7C3D08" },
];

// =========================== row primitives ===========================
export const FieldRow = ({ icon: Icon, label, value, mono = false, badge = null, isEditing = false, editor = null }) => (
  <div className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
    <span className="flex items-center gap-1.5 text-xs text-slate-500 whitespace-nowrap min-w-fit">
      {Icon && <Icon className="h-3.5 w-3.5 text-slate-400" />}
      {label}
    </span>
    {isEditing && editor ? (
      <div className="flex-1 max-w-[260px]">{editor}</div>
    ) : (
      <span className={`text-right text-sm font-medium text-slate-900 ${mono ? "font-mono text-xs" : ""}`}>
        {badge ? badge : (value || value === 0 ? value : <EmptyValue />)}
      </span>
    )}
  </div>
);

export const SectionCard = ({ icon: Icon, title, children, testId, className = "" }) => (
  <Card className={`shadow-sm border-slate-200 ${className}`} data-testid={testId}>
    <CardContent className="p-5">
      <h3 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-3">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {title}
      </h3>
      <div className="divide-y divide-slate-100">{children}</div>
    </CardContent>
  </Card>
);

// =========================== person avatar ===========================
export const PersonChip = ({ label, name, paletteIdx = 0 }) => {
  if (!name) return null;
  const p = PALETTES[paletteIdx % PALETTES.length];
  return (
    <div className="flex items-center gap-2.5 min-w-fit" data-testid={`person-chip-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div
        className="h-9 w-9 rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0"
        style={{ background: p.bg, color: p.fg }}
      >
        {initials(name)}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-900 leading-tight truncate">{name || "—"}</p>
        <p className="text-xs text-slate-500 leading-tight">{label}</p>
      </div>
    </div>
  );
};

// =========================== supplier status options ===========================
export const SUPPLIER_STATUS_OPTIONS = [
  { value: "", label: "— fără status —" },
  { value: "Signed", label: "Signed (semnat)" },
  { value: "Activated", label: "Activated (activ)" },
  { value: "Terminated", label: "Terminated (reziliat)" },
];

// =========================== notes_bo inline editor ===========================
export const NotesBoInline = ({ sale, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(sale?.notes_bo || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!sale?.id) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/sales/${sale.id}`, { notes_bo: draft || null });
      toast.success("Note salvate");
      setEditing(false);
      if (onSaved) await onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la salvare note");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="space-y-2">
        <Textarea
          rows={3}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="text-sm"
          autoFocus
          data-testid="overview-notes-bo-editor"
        />
        <div className="flex gap-2">
          <Button size="sm" onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="overview-notes-bo-save">
            {saving ? "Salvează..." : "Salvează"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setDraft(sale?.notes_bo || ""); setEditing(false); }} data-testid="overview-notes-bo-cancel">
            Anulează
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div
      onClick={() => setEditing(true)}
      className="text-sm rounded-md border border-dashed border-slate-200 p-2 whitespace-pre-wrap min-h-[2.5rem] cursor-pointer hover:border-indigo-300 hover:bg-slate-50 transition"
      title="Click pentru editare"
      data-testid="overview-notes-bo-readonly"
    >
      {sale?.notes_bo
        ? sale.notes_bo
        : <span className="text-slate-400 italic">— fără note (click pentru a adăuga) —</span>}
    </div>
  );
};
